import React from "react";
import { useHistory } from "react-router-dom";
import {
  Card,
  CardContent,
  Box,
  Button,
  Typography,
  IconButton,
} from "@mui/material";

const en = {
  thankYouCard: {
    title: "You’ve Just Made Waves",
    message: "Your feedback has reached us safely.",
    paragraph1: "Thank you for sharing your experience. Your insights help us maintain our world-class standards for every guest, whether at Wakatobi Resort or on the Pelagian Dive Yacht.",
    paragraph2: "Importantly, your visit is a vital contribution to our conservation efforts, directly supporting the protection of these pristine marine ecosystems and the local communities who care for them.",
    paragraph3: "We are incredibly grateful to have you as part of our story and look forward to welcoming you back to Wakatobi again.",
    backToHome: "Back To Home",
  },
};

const de = {
  thankYouCard: {
    title: "Sie haben gerade Wellen geschlagen",
    message: "Ihr Feedback hat uns sicher erreicht.",
    paragraph1: "Vielen Dank, dass Sie Ihre Erfahrungen geteilt haben. Ihre Einblicke helfen uns, unsere Weltklasse-Standards für jeden Gast aufrechtzuerhalten, sei es im Wakatobi Resort oder auf der Pelagian Dive Yacht.",
    paragraph2: "Wichtig ist, dass Ihr Besuch ein wesentlicher Beitrag zu unseren Naturschutzbemühungen ist und den Schutz dieser unberührten marinen Ökosysteme und der lokalen Gemeinschaften, die sich um sie kümmern, direkt unterstützt.",
    paragraph3: "Wir sind unglaublich dankbar, Sie als Teil unserer Geschichte zu haben und freuen uns darauf, Sie wieder in Wakatobi begrüßen zu dürfen.",
    backToHome: "Zurück zur Startseite",
  },
};

const translations = { en, de };

const ThankYouCard = ({shareText = 'Wakatobi was great!', shareUrl = 'https://wakatobi.interaktiv.sg/live', lang = 'en'}) => {
    const history = useHistory();
  
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: "#f5f5f5",
          position: 'relative',
          p: 2,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: {xs: "55%", md: "55%"},
            left: "55%",
            transform: "translate(-50%, -50%)",
            zIndex: 0,
            width: "95%",
            maxWidth: 650,
            pointerEvents: "none",
          }}
          component="span"
        >
          <svg
            width="100%"
            height="auto"
            viewBox="0 0 710 808"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g filter="url(#filter0_f_2540_1608)">
              <path
                d="M526.622 17L17.0005 44L33.9832 791H693L526.622 17Z"
                fill="url(#paint0_linear_2540_1608)"
              />
            </g>
            <defs>
              <filter
                id="filter0_f_2540_1608"
                x="0.000488281"
                y="0"
                width="710"
                height="808"
                filterUnits="userSpaceOnUse"
                colorInterpolationFilters="sRGB"
              >
                <feFlood floodOpacity="0" result="BackgroundImageFix" />
                <feBlend
                  mode="normal"
                  in="SourceGraphic"
                  in2="BackgroundImageFix"
                  result="shape"
                />
                <feGaussianBlur
                  stdDeviation="8.5"
                  result="effect1_foregroundBlur_2540_1608"
                />
              </filter>
              <linearGradient
                id="paint0_linear_2540_1608"
                x1="128.914"
                y1="60.7479"
                x2="501.812"
                y2="767.47"
                gradientUnits="userSpaceOnUse"
              >
                <stop stopColor="#B9B9B9" />
                <stop offset="1" stopColor="#454545" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
  
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "150px", // tinggi gradien
              background: "linear-gradient(to top, #f5f5f5, transparent)",
              zIndex: 1,
              pointerEvents: "none",
            }}
          />
        </Box>
  
  
        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: 550,
            textAlign: "center",
            background: `
              linear-gradient(315deg, transparent 50%, #C9CCD4 50%), 
              linear-gradient(135deg, #FFF, #FFF), 
              linear-gradient(135deg, #FFF, #FFF), 
              linear-gradient(315deg, transparent 39px, #FFF 10px)
            `,
            backgroundSize: "55px 55px, 0 0, 0 0, 100% 100%",
            backgroundPosition: "100% 100%, 0 0, 100% 100%, 100% 100%",
            backgroundRepeat: "no-repeat",
          }}
        >
        
          <Box
            component="img"
            src={require("../../static/img/paperclip.png")}
            alt="paperclip"
            sx={{
              position: "absolute",
              top: "-38px",
              left: "50%",
              transform: "translateX(-50%) scaleX(-1)",
              width: "70px",
              zIndex: 2,
            }}
          />
  
          {/* Top ocean background */}
          <Box
            sx={{
              width: "100%",
              height: { xs: 250, md: 320 },
              backgroundImage: `
                linear-gradient(to top, rgba(255,255,255,1) 5%, rgba(255,255,255,0) 100%),
                url(${require("../../static/img/ocean-bg.png")})
              `,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
  
          {/* Content */}
          <Box sx={{ p: { xs: 2, sm: 3, md: 3 } }}>
            {/* Heading */}
            <Box
              component="h4"
              sx={{
                color: "#101828",
                fontWeight: 600,
                fontSize: { xs: "2rem", md: "2.5rem" },
                lineHeight: 1.2,
                mb: 2,
              }}
            >
              {translations[lang]?.thankYouCard.title || translations.en.thankYouCard.title}
            </Box>
  
            {/* Subheading */}
            <Box
              component="p"
              sx={{
                color: "#667085",
                fontWeight: 400,
                fontSize: { xs: "1.1rem", md: "1.3rem" },
                mb: 2,
              }}
            >
              {translations[lang]?.thankYouCard.message || translations.en.thankYouCard.message}
            </Box>
  
            {/* Paragraph */}
            <Box
              component="p"
              sx={{
                fontSize: { xs: "1rem", md: "1.4rem" },
                fontWeight: 500,
                mb: 4,
                px: { xs: 1, sm: 3 },
                textAlign: 'left'
              }}
            >
              {translations[lang]?.thankYouCard.paragraph1 || translations.en.thankYouCard.paragraph1}
              <br/>
              <br/>
              {translations[lang]?.thankYouCard.paragraph2 || translations.en.thankYouCard.paragraph2}
              <br/>
              <br/>
              {translations[lang]?.thankYouCard.paragraph3 || translations.en.thankYouCard.paragraph3}
            </Box>
  
            {/* Logo */}
            <Box
              component="img"
              src={require("../../static/img/wakatobi-vertical.png")}
              alt="Wakatobi"
              sx={{
                width: { xs: "100px", sm: "140px", md: "160px" },
                mb: 3,
              }}
            />
          </Box>
        </Box>
  
        <Button
          variant="contained"
          onClick={() => {
            history.push("/");
            window.location.reload();
          }}
          sx={{
            mt: 2,
            px: { xs: 3, sm: 5 },
            py: { xs: 1, sm: 1.5 },
            fontSize: { xs: "1rem" },
            bgcolor: "#1e2eb8",
            borderRadius: "8px",
            
            "&:hover": { bgcolor: "#161d82" },
          }}
        >
          {translations[lang]?.thankYouCard.backToHome || translations.en.thankYouCard.backToHome}
        </Button>

        <Box sx={{ display: "flex", justifyContent: "center", gap: 3, mt: 4, position:'relative',
            zIndex: 10, }}>
          <Box
            component="img"
            src={require("../../static/img/social/facebook.png")}
            alt="Facebook"
            sx={{ width: 40, cursor: "pointer" }}
            onClick={() => {
              const shareWithMention = `${shareText} @WakatobiResort`;
              const encodedUrl = encodeURIComponent(shareUrl);
              const encodedQuote = shareWithMention;
              const hashtag = encodeURIComponent("#Wakatobi");
              const shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedQuote}&hashtag=${hashtag}`;
              window.open(shareLink, "_blank", "width=600,height=400");
            }}
          />

          {/* X / Twitter */}
          <Box
            component="img"
            src={require("../../static/img/social/X.png")}
            alt="X"
            sx={{ width: 40, cursor: "pointer" }}
            onClick={() => {
              const mention = "@WakatobiResort";
              const hashtags = "Wakatobi,Travel,Diving"; // comma-separated, no '#'
              const text = `${shareText} ${mention}`;
              const encodedText = encodeURIComponent(text);
              const encodedUrl = encodeURIComponent(shareUrl);

              const shareLink = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}&hashtags=${hashtags}`;
              window.open(shareLink, "_blank", "width=600,height=400");
            }}
          />


          {/* LinkedIn */}
          <Box
            component="img"
            src={require("../../static/img/social/linkedin.png")}
            alt="LinkedIn"
            sx={{ width: 40, cursor: "pointer" }}
            onClick={() =>
              window.open(
                `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}&summary=${shareText}`,
                "_blank"
              )
            }
          />

          {/* WhatsApp */}
          <Box
            component="img"
            src={require("../../static/img/social/whatsapp.png")}
            alt="WhatsApp"
            sx={{ width: 40, cursor: "pointer" }}
            onClick={() =>
              window.open(
                `https://api.whatsapp.com/send?text=${shareText}%20${shareUrl}`,
                "_blank"
              )
            }
          />

          {/* Instagram */}
          {/* <Box
            component="img"
            src={require("../../static/img/social/instagram.png")}
            alt="Instagram"
            sx={{ width: 40, cursor: "pointer", opacity: 0.5 }}
            title="Instagram does not support direct web sharing"
            onClick={() => alert("Instagram does not support sharing via web")}
          /> */}
        </Box>


      </Box>
    );
  };
export default ThankYouCard;
